/*
 * Fernwartung über Mesh an einem Touchpanel (hvp-contracts,
 * docs/wire-remote-session.md): Sitzung öffnen (RemoteSessionDialog),
 * laufende in drei Schritten sehen, verlängern, beenden. Der Server rechnet
 * die Freigabe und unterschreibt sie; hier wird nur gewählt, was der
 * Techniker erreichen will.
 *
 * Verbunden wird über den Helfer hvacloud-fw: der Link trägt nur Instanz
 * und Sitzung, den Schlüssel holt der Helfer selbst mit der Anmeldung des
 * Technikers. Der Befehl für ein installiertes Tailscale kommt genau einmal,
 * in der Antwort auf das Öffnen, und lebt nur in diesem Zustand.
 */
import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../api/client';
import { StatusBadge } from './ui/StatusBadge';
import { RemoteSessionDialog } from './RemoteSessionDialog';
import {
  helperLink, restText, ruleAction, sessionSteps,
  type OpenBody, type Rule, type RuleAction, type Service, type StepState,
} from './remoteSessionModel';

interface Session {
  id: string;
  mine: boolean;
  requestedBy: string;
  mode: 'read' | 'write';
  status: 'requested' | 'active' | 'ended';
  reason: string;
  startedAt: string;
  expiresAt: string;
  hardLimitAt: string;
  endedAt: string | null;
  endedReason: string | null;
  tailnetAddress: string | null;
  nodeName: string | null;
  technicianJoined: boolean;
  rules: Rule[];
}

interface ClientState {
  ready: boolean;
  missing: string[];
  canWrite: boolean;
  canOpen: boolean;
  busyByOther: boolean;
  sessions: Session[];
}

interface Omission {
  host: string | null;
  serviceKey: string;
  reason: string;
}

interface OpenResponse {
  session: Session;
  omissions: Omission[];
  technicianCommand: string;
}

/** Wo der Helfer für Windows liegt (Velopack, wie Outpost). */
const HELPER_DOWNLOAD = 'https://updates.sinnograte.at/fw-helfer/';

/** Laufzeit einer frischen Sitzung; der Server legt sie fest (RemoteAccess:SessionMinutes). */
const SESSION_MINUTES = 30;

const MISSING: Record<string, string> = {
  disabled: 'Die Fernwartung ist auf diesem Server ausgeschaltet.',
  headscale_not_configured: 'Headscale ist auf diesem Server nicht eingerichtet.',
  no_server_key: 'Auf diesem Server fehlt der Schlüssel für Fernwartungssitzungen.',
  no_capability: 'Das Panel meldet keine Fernwartung über Mesh. Sie braucht den Schalter FERNWARTUNG im TP-Configurator und eine aktuelle App.',
  no_device_key: 'Für das Panel ist noch kein Geräteschlüssel hinterlegt; er kommt mit der nächsten Zertifikatserneuerung.',
};

const ENDED: Record<string, string> = {
  user: 'beendet',
  admin: 'von einem Administrator beendet',
  expired: 'abgelaufen',
  hard_limit: 'Obergrenze von 4 h erreicht',
  activation_timeout: 'Panel hat nicht rechtzeitig geantwortet',
  device_lost: 'Verbindung zum Panel verloren',
  key_reuse: 'Beitrittsschlüssel zweimal abgerufen, aus Sicherheitsgründen beendet',
  server: 'vom Server beendet',
  'device:local': 'am Panel beendet',
  'device:expired': 'am Panel abgelaufen',
  'device:heartbeat': 'am Panel beendet, ohne Lebenszeichen der Cloud',
  'device:not_running': 'am Panel nicht mehr aktiv',
  'device:not_enabled': 'am Panel ist die Fernwartung ausgeschaltet',
  'device:busy': 'am Panel läuft schon eine andere Sitzung',
  'device:not_ready': 'tailscale lädt am Panel noch',
};

const OPEN_ERRORS: Record<string, string> = {
  client_busy: 'Am Panel läuft schon eine Sitzung.',
  user_busy: 'Du hast schon eine offene Sitzung an einem anderen Gerät.',
  writer_busy: 'An dieser Anlage arbeitet schon jemand schreibend.',
  no_capability: MISSING.no_capability,
  no_device_key: MISSING.no_device_key,
  no_server_key: MISSING.no_server_key,
  disabled: MISSING.disabled,
  reason_required: 'Bitte einen Grund angeben.',
};

const STEP_TONE: Record<StepState, 'ok' | 'info' | 'neutral'> = { done: 'ok', current: 'info', waiting: 'neutral' };

function endedText(reason: string | null): string {
  if (!reason) return 'beendet';
  return ENDED[reason] ?? (reason.startsWith('device:') ? `am Panel beendet (${reason.slice(7)})` : reason);
}

function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { reason?: string; message?: string } | null;
    if (body?.reason && OPEN_ERRORS[body.reason]) return OPEN_ERRORS[body.reason];
    if (body?.message) return body.message;
    if (e.status === 403) return 'Dafür fehlt dir das Recht.';
    return `Fehler ${e.status}.`;
  }
  return 'Server nicht erreichbar.';
}

function copy(text: string) {
  void navigator.clipboard?.writeText(text);
}

function ActionCell({ action }: { action: RuleAction | null }) {
  if (!action) return <span class="muted">wartet auf das Panel</span>;
  if (action.kind === 'web') {
    return <a class="hv-btn hv-btn--sm" href={action.url} target="_blank" rel="noopener noreferrer">Öffnen</a>;
  }
  if (action.kind === 'scope') {
    return (
      <span class="hv-remote-session__scope">
        <span>Host <code class="mono">{action.host}</code></span>
        <span>Port <code class="mono">{action.port}</code></span>
        {action.apiPort !== null && <span>Device API <code class="mono">{action.apiPort}</code></span>}
        <button type="button" class="hv-btn hv-btn--sm hv-btn--ghost" onClick={() => copy(action.host)}>Host kopieren</button>
      </span>
    );
  }
  return (
    <span>
      <code class="mono">{action.text}</code>{' '}
      <button type="button" class="hv-btn hv-btn--sm hv-btn--ghost" onClick={() => copy(action.text)}>Kopieren</button>
    </span>
  );
}

export function RemoteSessionPanel({ plantId, clientId, clientLabel }: { plantId: string; clientId: string; clientLabel?: string }) {
  const base = `/api/plants/${plantId}/clients/${clientId}/remote-sessions`;
  const [state, setState] = useState<ClientState | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [command, setCommand] = useState<{ sessionId: string; text: string } | null>(null);
  const [omissions, setOmissions] = useState<Omission[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [now, setNow] = useState(Date.now());

  async function reload() {
    try {
      setState(await api.get<ClientState>(base));
      setLoadError(null);
    } catch (e) {
      setLoadError(errorText(e));
    }
  }

  useEffect(() => {
    void reload();
    api.get<Service[]>('/api/remote-access/services').then(setServices).catch(() => setServices([]));
  }, [plantId, clientId]);

  const open = state?.sessions.find((s) => s.status !== 'ended') ?? null;

  // Während eine Sitzung läuft, Zustand nachladen und die Restzeit zählen.
  // Bis der Techniker verbunden ist, öfter: darauf wartet jemand.
  useEffect(() => {
    if (!open) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const fast = open.status === 'requested' || !open.technicianJoined;
    const poll = setInterval(() => void reload(), fast ? 3000 : 15000);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, [open?.id, open?.status, open?.technicianJoined]);

  async function submit(body: OpenBody) {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<OpenResponse>(base, body);
      setCommand({ sessionId: res.session.id, text: res.technicianCommand });
      setOmissions(res.omissions);
      setDialogOpen(false);
      await reload();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function act(kind: 'extend' | 'end', id: string) {
    setBusy(true);
    setError(null);
    try {
      if (kind === 'extend') await api.post(`/api/remote-sessions/${id}/extend`);
      else await api.delete(`/api/remote-sessions/${id}`);
      if (kind === 'end') setCommand(null);
      await reload();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  if (loadError) return <p class="muted">{loadError}</p>;
  if (!state) return <p class="muted">Lade Fernwartung…</p>;

  if (open) {
    const cmd = command?.sessionId === open.id ? command.text : null;
    const [s1, s2, s3] = sessionSteps(open.status, open.technicianJoined);
    const canExtend = open.mine && open.status === 'active'
      && new Date(open.expiresAt).getTime() < new Date(open.hardLimitAt).getTime();
    return (
      <div class="hv-remote-session">
        <p>
          {open.mode === 'write' ? 'Schreibend' : 'Lesend'}, noch <b>{restText(open.expiresAt, now)}</b>
          {!open.mine && <span class="muted"> · {open.requestedBy}</span>}
        </p>
        <p class="muted">Grund: {open.reason}</p>

        <ol class="hv-remote-session__steps">
          <li class={`is-${s1}`}>
            <StatusBadge tone={STEP_TONE[s1]}>1</StatusBadge>{' '}
            <b>Panel verbindet</b>{' '}
            {open.status === 'requested'
              ? <span class="muted">Panel tritt dem Tailnet bei…</span>
              : <span class="muted">im Tailnet als <code class="mono">{open.tailnetAddress}</code>{open.nodeName && ` (${open.nodeName})`}</span>}
          </li>
          <li class={`is-${s2}`}>
            <StatusBadge tone={STEP_TONE[s2]}>2</StatusBadge>{' '}
            <b>Techniker verbinden</b>{' '}
            {open.technicianJoined
              ? <span class="muted">Techniker verbunden</span>
              : open.mine && (
                <span class="hv-remote-session__connect">
                  <a class="hv-btn hv-btn--sm hv-btn--primary" href={helperLink(window.location.host, open.id)}>Verbinden</a>
                  <a class="hv-btn hv-btn--sm hv-btn--ghost" href={HELPER_DOWNLOAD} target="_blank" rel="noopener noreferrer">
                    Helfer herunterladen
                  </a>
                </span>
              )}
            {open.mine && !open.technicianJoined && (
              <p class="muted">„Verbinden“ öffnet den Helfer hvacloud-fw. Er meldet dich an, tritt bei und legt jedes Gerät auf deinem Rechner ab.</p>
            )}
          </li>
          <li class={`is-${s3}`}>
            <StatusBadge tone={STEP_TONE[s3]}>3</StatusBadge>{' '}
            <b>Arbeiten</b>
            {open.rules.length > 0 ? (
              <table class="hv-remote-session__rules">
                <thead><tr><th>Ziel an LAN2</th><th>Dienst</th><th>Mit Helfer</th></tr></thead>
                <tbody>
                  {open.rules.map((r) => (
                    <tr key={r.listenPort}>
                      <td class="mono">{r.targetHost}:{r.targetPort}</td>
                      <td>{r.serviceKey}</td>
                      <td><ActionCell action={ruleAction(r, open.rules, 'helper', open.tailnetAddress)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p class="muted">Keine Weiterleitung an LAN2, nur das Panel selbst.</p>}
            {open.rules.length > 0 && (
              <p class="muted">Belegt ein anderes Programm einen Port, nimmt der Helfer einen freien und zeigt ihn in seinem Fenster.</p>
            )}
          </li>
        </ol>

        {omissions.length > 0 && command?.sessionId === open.id && (
          <ul class="hv-remote-session__omissions">
            {omissions.map((o) => <li key={`${o.host}-${o.serviceKey}`}>{o.host} {o.serviceKey}: {o.reason}</li>)}
          </ul>
        )}

        {open.mine && (
          <details class="hv-remote-session__fallback">
            <summary>Ohne Helfer verbinden (installiertes Tailscale)</summary>
            {cmd ? (
              <div class="hv-remote-session__cmd">
                <p>Auf deinem Rechner ausführen (einmal gültig, 15 min; wird nicht noch einmal angezeigt):</p>
                <pre class="mono">{cmd}</pre>
                <button type="button" class="hv-btn hv-btn--sm" onClick={() => copy(cmd)}>Kopieren</button>
              </div>
            ) : (
              <p class="muted">Der Befehl steht nur direkt nach dem Öffnen da. Danach geht es nur noch über den Helfer.</p>
            )}
            {open.rules.length > 0 && (
              <table class="hv-remote-session__rules">
                <thead><tr><th>Ziel an LAN2</th><th>Dienst</th><th>Über das Tailnet</th></tr></thead>
                <tbody>
                  {open.rules.map((r) => (
                    <tr key={r.listenPort}>
                      <td class="mono">{r.targetHost}:{r.targetPort}</td>
                      <td>{r.serviceKey}</td>
                      <td><ActionCell action={ruleAction(r, open.rules, 'tailnet', open.tailnetAddress)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </details>
        )}

        <p class="hv-remote-session__actions">
          {canExtend && (
            <button type="button" class="hv-btn hv-btn--sm" disabled={busy} onClick={() => void act('extend', open.id)}>
              Um 30 min verlängern
            </button>
          )}
          <button type="button" class="hv-btn hv-btn--sm hv-btn--danger-outline" disabled={busy}
                  onClick={() => void act('end', open.id)}>Beenden</button>
        </p>
        {error && <p class="hv-remote-session__error">{error}</p>}
      </div>
    );
  }

  const last = state.sessions[0];
  if (!state.ready) {
    return (
      <div class="hv-remote-session">
        {state.missing.map((m) => <p key={m} class="muted">{MISSING[m] ?? m}</p>)}
        {last && <p class="muted">Letzte Sitzung: {endedText(last.endedReason)}</p>}
      </div>
    );
  }
  if (!state.canOpen) return <p class="muted">Dafür fehlt dir das Recht remote.session.read.</p>;
  return (
    <div class="hv-remote-session">
      {last && <p class="muted">Letzte Sitzung: {endedText(last.endedReason)}, {new Date(last.startedAt).toLocaleString('de-AT')}</p>}
      <button type="button" class="hv-btn hv-btn--sm hv-btn--primary"
              onClick={() => { setError(null); setDialogOpen(true); }}>Fernwartung starten</button>
      {dialogOpen && (
        <RemoteSessionDialog
          plantId={plantId} clientId={clientId} clientLabel={clientLabel ?? 'Touchpanel'}
          services={services} canWrite={state.canWrite} busyByOther={state.busyByOther}
          sessionMinutes={SESSION_MINUTES} busy={busy} error={error}
          onSubmit={(b) => void submit(b)} onClose={() => setDialogOpen(false)} />
      )}
    </div>
  );
}
